import * as React from 'react';
import { PRODUCT_NAME } from '@reos/i18n';

/**
 * Krok BRAND2 — ZNACKA PRODUKTU NA OBRAZOVCE JE LOGO „NAVES".
 *
 * Do BRAND1 tu stalo DOCASNE TEXTOVE logo (jmeno pismem Manrope), protoze
 * logo jeste nedorazilo. Od BRAND2 se kresli skutecne logo z manualu
 * (`docs/design/brand-naves/`, R.B. 6. 10. 2026) — soubory lezi v
 * `public/brand/` a jsou BAJTOVE KOPIE balicku (hlida `brand2-identita.spec.ts`).
 *
 * PROC JEDEN `<img>` A NE DVA SCHOVANE CSS (zadani BRAND2 bod 1 — „přepínání
 * podle režimu, ne dvě img naráz"):
 *   * dva uzly = dva requesty na obrazek, dva tab-stopy pro ctecku a dve
 *     stejna `data-testid` (strict mode Playwrightu),
 *   * `src` nejde prepnout CSS, protoze rezim adminu NENI `prefers-color-scheme`
 *     — uzivatel ma volbu „Světlý · Tmavý · Podle systému" a vysledek zije
 *     v atributu `data-scheme` na `<html>` (`stores/session.ts`).
 * Proto se rezim CTE z toho atributu a komponenta se prekresli, kdyz se zmeni.
 *
 * MINIMALNI VELIKOSTI (manual str. 3): horizontalni logo 140 px na sirku,
 * samostatna znacka v ovladani aspon 24 px. Obe jsou tady jako spodni mez
 * vychozich hodnot — volajici je smi jen ZVETSIT.
 */

/** Pomer stran horizontalniho loga ze souboru (`828.816 × 190`). */
const HORIZONTAL_RATIO = 828.816 / 190;

/** Manual str. 3: „Minimum v UI: horizontální logo 140 px na šířku." */
export const LOGO_MIN_WIDTH_PX = 140;

/** Manual str. 3: „Samostatná značka … v ovládání alespoň 24 px." */
export const SYMBOL_MIN_SIZE_PX = 24;

/**
 * Ucinny rezim adminu. Cte se PRI VYKRESLENI (ne v efektu po pripojeni, jinak
 * by prvni snimek mel vzdy svetle logo) a sleduje se zmena atributu — prepnuti
 * ve Nastaveni → Vzhled meni atribut, ne stav Reactu.
 */
function subscribeScheme(onChange: () => void): () => void {
  if (typeof MutationObserver !== 'function') return () => undefined;
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-scheme'] });
  return () => observer.disconnect();
}

function readScheme(): 'light' | 'dark' {
  return document.documentElement.getAttribute('data-scheme') === 'dark' ? 'dark' : 'light';
}

export function useColorScheme(): 'light' | 'dark' {
  // `useSyncExternalStore` cte hodnotu pri kazdem vykresleni, takze tu nemuze
  // vzniknout zavod „stav z efektu po pripojeni" (nalez kroku FIXE1).
  return React.useSyncExternalStore(subscribeScheme, readScheme, () => 'light');
}

/**
 * Horizontalni logo (symbol + nazev). Svetla varianta na kremove/bile plose,
 * tmava na grafitu — manual str. 2 a `CTETE-ME.md` § Použití.
 */
export function ProductLogo({
  className,
  testId,
  widthPx = LOGO_MIN_WIDTH_PX,
}: {
  readonly className?: string | undefined;
  readonly testId?: string | undefined;
  readonly widthPx?: number | undefined;
}): React.JSX.Element {
  const scheme = useColorScheme();
  const width = Math.max(widthPx, LOGO_MIN_WIDTH_PX);
  return (
    <img
      className={className === undefined ? 'rh-a2-logo' : `rh-a2-logo ${className}`}
      data-testid={testId}
      src={`/brand/maklerov-naves-horizontalni-${scheme === 'dark' ? 'tmave' : 'svetle'}.svg`}
      alt={PRODUCT_NAME}
      width={width}
      height={Math.round(width / HORIZONTAL_RATIO)}
    />
  );
}

/**
 * Samostatna znacka bez nazvu — pro mista, kde na 140 px neni sirka
 * (ovladaci prvky, drobny podpis). Manual str. 11: zadne stiny, gradienty
 * ani prebarvovani, proto se bere hotovy soubor a meni se jen velikost.
 */
export function ProductSymbol({
  className,
  testId,
  sizePx = SYMBOL_MIN_SIZE_PX,
}: {
  readonly className?: string | undefined;
  readonly testId?: string | undefined;
  readonly sizePx?: number | undefined;
}): React.JSX.Element {
  const scheme = useColorScheme();
  const size = Math.max(sizePx, SYMBOL_MIN_SIZE_PX);
  return (
    <img
      className={className === undefined ? 'rh-a2-symbol' : `rh-a2-symbol ${className}`}
      data-testid={testId}
      src={`/brand/maklerov-naves-symbol-${scheme === 'dark' ? 'tmave' : 'svetle'}.svg`}
      alt={PRODUCT_NAME}
      width={size}
      height={size}
    />
  );
}
